/**
 * IncrementContentViewsUseCase
 *
 * Increments the views count for a content item via the
 * `incrementContentViews` cloud function.
 *
 * Flutter sources of truth:
 *   - lib/data/repository/medias_repository.dart:373-375
 *   - lib/services/cloud_functions_service.dart:1048-1067
 *   - lib/presentation/view_models/content_view_model.dart:347-361
 *     (caller — gating + once-per-session flag)
 *
 * Fire-and-forget contract: errors are logged and swallowed (mirrors Flutter).
 */

import { MediaService } from '@/domain/services/media/mediaService';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class IncrementContentViewsUseCase {
  private mediaService: MediaService;

  constructor(mediaService?: MediaService) {
    this.mediaService = mediaService || new MediaService();
  }

  /**
   * Execute the use case.
   * Returns early without calling when userId or contentId is empty.
   * Returns the success flag from the cloud function (false on any error).
   * Never throws — errors are logged and swallowed.
   */
  async execute(userId: string, contentId: string): Promise<boolean> {
    if (!userId || !contentId) {
      return false;
    }

    try {
      return await this.mediaService.incrementContentViews(userId, contentId);
    } catch (error) {
      appLogger.error(
        'Error executing IncrementContentViewsUseCase',
        'IncrementContentViewsUseCase', { error: error as Error },
      );
      return false;
    }
  }
}

export const incrementContentViewsUseCase = new IncrementContentViewsUseCase();
